import { staffSessionHeaders, getStaffSessionToken } from '../modules/auth/staffSession';
import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  fetchBaseQuery
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/state';
import { toast } from 'react-toastify';
import { clearToken } from '../modules/auth/state/authSlice';

export const isDev = process.env.NODE_ENV === 'development';

// API Base URLs from environment variables
const DEV_BASE_URL = process.env.REACT_APP_DEV_BASE_URL || 'http://192.168.1.85:3001/';
const PROD_BASE_URL = process.env.REACT_APP_PROD_BASE_URL || 'https://new-test-w4ono.ondigitalocean.app';

// Select the appropriate base URL based on environment
const BASE_URL = isDev ? DEV_BASE_URL : PROD_BASE_URL;


const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  timeout: 70000, // 70 seconds timeout for frontend requests
  prepareHeaders: (headers, api) => {
    if (['teamGoogleLogin', 'login', 'requestLogin'].includes(api.endpoint)) return headers;
    Object.entries(staffSessionHeaders()).forEach(([key, value]) => headers.set(key, value));
    const { token } = (api.getState() as RootState).auth;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  // Snapshot endpoints surface their own inline errors / empty states; the
  // global toast is reserved for auth + critical infrastructure failures.
  const requestUrl = typeof args === 'string' ? args : args.url;
  const isSnapshotRequest =
    typeof requestUrl === 'string' &&
    (requestUrl.includes('/cameras/snapshot') ||
      requestUrl.includes('/admin/router-snapshot'));

  if (result.error && result.error.status === 403) {
    toast.error('Unauthorized access, please login again');
  }
  if (
    result.error &&
    Number(result.error.status) >= 500 &&
    Number(result.error.status) < 600 &&
    !isSnapshotRequest
  ) {
    toast.error('Server error, please try again later');
  }

  if (result.error && result.error.status === 401) {
    // Logic to refresh token
    const wasStaff = Boolean(getStaffSessionToken()) || (api.getState() as RootState).auth.isAdmin;
    api.dispatch(clearToken());
    window.location.href = wasStaff ? '/team-login' : '/';
    toast('Session expired, please login again');
  }
  return result;
};
